import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import FeedbackPin from './FeedbackPin';
import { usePrototypePreview } from './PrototypePreviewContext';
import PrototypeSideBySidePanel from './PrototypeSideBySidePanel';

import styles from './styles.module.scss';

const CATEGORY_MAPPING = {
  error: { class: 'error' },
  suggestion: { class: 'warning' },
  positive: { class: 'positive' },
  'deeper-insights': { class: 'deeper-insights' },
};

const ImageCarousel = ({
  feedback,
  images,
  onBackClick,
  selectedImageIndex = 0,
  onImageIndexChange,
  onPinClick,
  isLoading = false,
  activeInsightUuid = null,
}) => {
  const [currentImageDimensions, setCurrentImageDimensions] = useState(null);
  // Rendered CSS pixels captured on load — locks both panels to the same size
  const [renderedImageDimensions, setRenderedImageDimensions] = useState(null);
  const [thumbnailsOverflow, setThumbnailsOverflow] = useState(false);
  const [showOriginalEntryAnimation, setShowOriginalEntryAnimation] = useState(false);
  const currentImageAspectRatio = currentImageDimensions
    ? currentImageDimensions.width / currentImageDimensions.height
    : null;
  const thumbnailsWrapperRef = useRef(null);
  const thumbnailsInnerRef = useRef(null);
  const imageRef = useRef(null);
  const rowContainerRef = useRef(null);

  const { getPrototypeForPage, getPanelsForPage } = usePrototypePreview();

  const currentPage = images[selectedImageIndex]?.page ?? selectedImageIndex + 1;
  const prototypeState = getPrototypeForPage(currentPage, activeInsightUuid);
  const hasPrototype = Boolean(prototypeState);
  const prototypePanels = getPanelsForPage(currentPage, activeInsightUuid);
  const prevHasPrototypeRef = useRef(hasPrototype);

  // Landscape = aspect ratio >= 1 (width >= height); portrait = ratio < 1.
  const isLandscape = currentImageAspectRatio !== null && currentImageAspectRatio !== undefined
    ? currentImageAspectRatio >= 1
    : null;

  // Choose the layout class once to avoid a nested ternary in JSX
  const layoutClass = hasPrototype
    ? (
      isLandscape === true
        ? styles['image-row--stacked']
        : isLandscape === false
          ? styles['image-row--side-by-side']
          : 'flex-col gap-4'
    )
    : 'flex-col gap-4';

  const thumbnailIndices = useMemo(
    () => [...Array(images.length).keys()],
    [images.length]
  );

  // Detect if thumbnails exceed parent container width
  useEffect(() => {
    const wrapper = thumbnailsWrapperRef.current;
    const inner = thumbnailsInnerRef.current;

    if (!wrapper || !inner || !images.length) {
      setThumbnailsOverflow(false);

      return;
    }

    const checkOverflow = () => {
      const availableWidth = wrapper.clientWidth - 32;
      const contentWidth = inner.scrollWidth;

      setThumbnailsOverflow(contentWidth > Math.max(0, availableWidth));
    };

    const rafId = requestAnimationFrame(() => {
      checkOverflow();
    });

    // eslint-disable-next-line compat/compat -- ResizeObserver used for thumbnail overflow detection
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(checkOverflow);
    });

    resizeObserver.observe(wrapper);

    resizeObserver.observe(inner);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [images.length]);

  useEffect(() => {
    setCurrentImageDimensions(null);
    setRenderedImageDimensions(null);

    if (rowContainerRef.current) {
      rowContainerRef.current.scrollTop = 0;
      rowContainerRef.current.scrollLeft = 0;
    }
  }, [selectedImageIndex]);

  useEffect(() => {
    setShowOriginalEntryAnimation(false);
    prevHasPrototypeRef.current = hasPrototype;
  }, [selectedImageIndex]);

  useEffect(() => {
    if (hasPrototype && !prevHasPrototypeRef.current) {
      setShowOriginalEntryAnimation(true);
    }
    else if (!hasPrototype) {
      setShowOriginalEntryAnimation(false);
    }

    prevHasPrototypeRef.current = hasPrototype;
  }, [hasPrototype]);

  // Get feedback items for current page with pin locations, handling dismissed findings and overlapping positions
  const currentPageFeedback = useMemo(() => {
    if (!feedback || !Array.isArray(feedback) || !images[selectedImageIndex]) {
      return [];
    }

    const currentImagePage = images[selectedImageIndex].page || selectedImageIndex + 1;

    // Filter for current page, exclude dismissed findings, and only include items with pin locations
    const activeFeedback = feedback.filter(
      item => item.page === currentImagePage
      && !item.dismiss
      && item.dismiss !== true
      && item.pin_location
    );

    // Handle overlapping pin positions
    const positionTracker = new Map();

    return activeFeedback.map(feedbackItem => {
      const pinLocation = feedbackItem.pin_location;

      const positionKey = `${pinLocation.x_percent}-${pinLocation.y_percent}`;

      // Find the original index of this feedback item in the full feedback array
      const originalIndex = feedback.findIndex(item => item === feedbackItem);
      const feedbackNumber = originalIndex + 1; // Convert to 1-based numbering

      // Determine category and severity class (matching FeedbackBox logic)
      const getCategory = item => {
        if (item.isDeeperInsight) {
          return 'deeper-insights';
        }

        return item.category || '';
      };

      const category = getCategory(feedbackItem);
      const severityClass = category && CATEGORY_MAPPING[category]
        ? CATEGORY_MAPPING[category].class
        : '';

      if (positionTracker.has(positionKey)) {
        // If position is already used, offset the new pin to the right
        const offset = positionTracker.get(positionKey) * 8; // 5% offset for each overlapping pin

        positionTracker.set(positionKey, positionTracker.get(positionKey) + 1);

        return {
          ...feedbackItem,
          feedbackIndex: originalIndex,
          feedbackNumber,
          severityClass,
          adjustedLocation: {
            x_percent: Math.min(pinLocation.x_percent + offset, 95), // Don't go beyond 95% to avoid edge cutoff
            y_percent: pinLocation.y_percent,
          },
        };
      }
      positionTracker.set(positionKey, 1);

      return {
        ...feedbackItem,
        feedbackIndex: originalIndex,
        feedbackNumber,
        severityClass,
        adjustedLocation: pinLocation,
      };
    });
  }, [feedback, selectedImageIndex, images]);

  const handleThumbnailClick = index => {
    // Allow clicking on any page index
    handleImageSelect(index);
  };

  if (isLoading) {
    return (
      <div className={cn(styles['detail-image-wrapper'])}>
        <div className={styles['detail-image-placeholder']}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <div
              style={{
                width: '16px',
                height: '16px',
                border: '2px solid #E2E8F0',
                borderTop: '2px solid #3B82F6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            Loading images...
          </div>
        </div>
      </div>
    );
  }

  if (!images || images.length === 0) {
    return (
      <div className={cn(styles['detail-image-wrapper'])}>
        <div className={styles['detail-image-placeholder']}>
          No images available
        </div>
      </div>
    );
  }

  const safeIndex = selectedImageIndex >= 0 && selectedImageIndex < images.length
    ? selectedImageIndex
    : 0;
  const currentImage = images[safeIndex];

  return (
    <>
      <div className={styles['detail-preview-header']}>
        <button
          onClick={onBackClick}
          className={styles['detail-back-button']}
        >
          ←
        </button>
        {currentImage?.page && (
          <span className={styles['detail-page-indicator']}>
            {currentImage.page}
            {' '}
            of
            {' '}
            {images.length}
          </span>
        )}
      </div>
      <div className={cn('flex flex-1 flex-col gap-4 overflow-auto')}>
        <div
          ref={rowContainerRef}
          className={cn('flex flex-1 min-h-0', layoutClass)}
        >
          <div
            className={cn(
              styles['detail-image-wrapper'],
              hasPrototype && isLandscape === false && styles['detail-image-wrapper--with-prototype'],
              hasPrototype && isLandscape === true && styles['detail-image-wrapper--with-prototype-landscape'],
              showOriginalEntryAnimation && isLandscape === false && styles['original-slide-to-left'],
              showOriginalEntryAnimation && isLandscape === true && styles['original-slide-to-top']
            )}
            style={
              hasPrototype && renderedImageDimensions
                ? {
                  width: renderedImageDimensions.width + 32,
                  height: renderedImageDimensions.height + 32,
                  flexShrink: 0,
                }
                : {}
            }
          >
            {hasPrototype && (
              <p className={cn(styles['screen-label'], styles['screen-label--original'])}>
                Original
              </p>
            )}
            <div className={cn(styles['inner'], { [styles['full-height']]: currentImageAspectRatio < 1 })}>
              <img
                ref={imageRef}
                key={safeIndex}
                src={currentImage.url}
                alt={currentImage.alt || `Design ${safeIndex + 1}`}
                className={cn(
                  styles['detail-image'],
                  hasPrototype && styles['detail-image--canvas']
                )}
                loading={safeIndex <= 1 ? 'eager' : 'lazy'}
                onLoad={e => {
                  const { naturalWidth, naturalHeight } = e.target;

                  setCurrentImageDimensions({ width: naturalWidth, height: naturalHeight });

                  const imgEl = imageRef.current || e.target;
                  const rect = imgEl.getBoundingClientRect();

                  if (rect.width > 0 && rect.height > 0) {
                    setRenderedImageDimensions({
                      width: Math.round(rect.width),
                      height: Math.round(rect.height),
                    });
                  }
                  else {
                    // Fallback: calculate from container dimensions
                    const rowEl = rowContainerRef.current;
                    const availableW = Math.max((rowEl?.clientWidth ?? 0) - 32, 0);
                    const availableH = Math.max((rowEl?.clientHeight ?? 0) - 32, 0);

                    if (availableW > 0 && availableH > 0 && naturalWidth > 0 && naturalHeight > 0) {
                      const fitScale = Math.min(
                        availableW / naturalWidth,
                        availableH / naturalHeight,
                        1
                      );

                      setRenderedImageDimensions({
                        width: Math.round(naturalWidth * fitScale),
                        height: Math.round(naturalHeight * fitScale),
                      });
                    }
                  }
                }}
              />
              {currentPageFeedback.map((feedbackItem, index) => (
                <FeedbackPin
                  key={`${feedbackItem.id || index}-pin`}
                  number={feedbackItem.feedbackNumber}
                  x={feedbackItem.adjustedLocation.x_percent}
                  y={feedbackItem.adjustedLocation.y_percent}
                  severity={feedbackItem.severityClass}
                  onClick={
                    onPinClick && feedbackItem.feedbackIndex != null
                      ? () => onPinClick(feedbackItem.feedbackIndex)
                      : undefined
                  }
                />
              ))}
            </div>
          </div>
          {prototypePanels.map((panel, idx) => (
            <PrototypeSideBySidePanel
              key={panel.key || `panel-${idx}`}
              remixStatus={panel.status}
              previewSrc={panel.previewSrc}
              htmlUrl={panel.htmlUrl}
              processUuid={panel.processUuid}
              variantIndex={idx}
              refinementPrompt={panel.refinementPrompt}
              renderedDimensions={renderedImageDimensions}
              isLandscape={isLandscape === true}
            />
          ))}
        </div>
        <div
          ref={thumbnailsWrapperRef}
          className={cn(styles['detail-thumbnails-wrapper'], {
            [styles['detail-thumbnails-wrapper--scrollable']]: thumbnailsOverflow,
          })}
        >
          <div
            ref={thumbnailsInnerRef}
            className={cn(styles['detail-thumbnails'], {
              [styles['detail-thumbnails--scrollable']]: thumbnailsOverflow,
            })}
          >
            {thumbnailIndices.map(index => {
              const image = images[index];
              const isSelected = index === safeIndex;

              return (
                <button
                  key={index}
                  type="button"
                  className={cn(styles['detail-thumbnail-item'], {
                    [styles['detail-thumbnail-item--selected']]: isSelected,
                  })}
                  onClick={() => handleThumbnailClick(index)}
                  aria-label={`Go to image ${index + 1}`}
                  aria-current={isSelected ? 'true' : undefined}
                >
                  <div
                    className={cn(styles['detail-thumbnail-box'], {
                      [styles['detail-thumbnail-box--selected']]: isSelected,
                    })}
                  >
                    <img
                      src={image.url}
                      alt={image.alt || `Design ${index + 1}`}
                      className={styles['detail-thumbnail-img']}
                      loading="lazy"
                    />
                  </div>
                  <div className={styles['detail-thumbnail-number']}>
                    {index + 1}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );

  // Handle image selection to show relevant feedback
  function handleImageSelect(imageIndex) {
    if (onImageIndexChange) {
      onImageIndexChange(imageIndex);
    }
  }
};

ImageCarousel.propTypes = {
  feedback: PropTypes.array.isRequired,
  images: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    url: PropTypes.string.isRequired,
    page: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    alt: PropTypes.string,
  })),
  onBackClick: PropTypes.func.isRequired,
  selectedImageIndex: PropTypes.number,
  onImageIndexChange: PropTypes.func,
  onPinClick: PropTypes.func,
  isLoading: PropTypes.bool,
  activeInsightUuid: PropTypes.string,
};

ImageCarousel.defaultProps = {
  feedback: [],
  images: [],
  onBackClick: () => {},
};

export default ImageCarousel;
